import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import Toast from "../../../Components/Toast/Toast";
import toArrayBuffer from "to-array-buffer";

const splitLinesToArray = (value) => {
  return (value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const splitCommaToArray = (value) => {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export default function AddPartner() {
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({
    partnerId: "",
    partnerName: "",
  });
  const [deleting, setDeleting] = useState(false);

  const [partnerName, setPartnerName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [category, setCategory] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [servicesInput, setServicesInput] = useState("");
  const [howToUseInput, setHowToUseInput] = useState("");
  const [termsInput, setTermsInput] = useState("");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState({
    icon: "",
    message: "",
    iconColor: "",
    cName: "",
  });

  const isEditMode = Boolean(selectedPartnerId);

  const payload = useMemo(() => {
    return {
      partnerName: partnerName.trim(),
      imageUrl: imageUrl.trim(),
      category: category.trim(),
      promoCode: promoCode.trim(),
      services: splitCommaToArray(servicesInput),
      howToUse: splitLinesToArray(howToUseInput),
      termsAndConditions: splitLinesToArray(termsInput),
      status,
    };
  }, [
    partnerName,
    imageUrl,
    category,
    promoCode,
    servicesInput,
    howToUseInput,
    termsInput,
    status,
  ]);

  const showToast = (type, message) => {
    setToast({
      icon: type === "success" ? faCheckCircle : faTimesCircle,
      message,
      iconColor: "",
      cName: type === "success" ? "show show-success" : "show show-danger",
    });
    setTimeout(() => {
      setToast({ icon: "", message: "", iconColor: "", cName: "" });
    }, 1800);
  };

  const resetForm = () => {
    setSelectedPartnerId("");
    setPartnerName("");
    setImageUrl("");
    setCategory("");
    setPromoCode("");
    setServicesInput("");
    setHowToUseInput("");
    setTermsInput("");
    setStatus("active");
    setErrorMessage("");
    setFormErrors({});
    setLoading(false);
    setUploadingImage(false);
    setImagePreviewError(false);
  };

  const setFieldError = (field, message) => {
    setFormErrors((prev) => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setFormErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!payload.partnerName) errors.partnerName = "Partner name is required";
    if (!payload.category) errors.category = "Category is required";

    // Image is required for add & edit (to satisfy backend expectations)
    if (!payload.imageUrl) errors.imageUrl = "Partner image is required";

    if (uploadingImage) errors.imageUrl = "Please wait for image upload to finish";

    return errors;
  };

  useEffect(() => {
    setImagePreviewError(false);
  }, [imageUrl]);

  const handleOpenAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setLoading(false);
    resetForm();
  };

  const uploadImage = async (file) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);

    const uploaderId =
      localStorage.getItem("editorId") ||
      "a80422ab-1f3c-4676-8200-fc38a156599f";

    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const fileRes = btoa(reader.result);
          const bytes = toArrayBuffer(fileRes);

          const response = await axios.post(
            `${API_CONSTANTS.BASEURL}admin/image-upload-api/user/${uploaderId}/type/new`,
            bytes,
            {
              headers: API_CONSTANTS.imgheaders(),
            }
          );

          resolve(response?.data?.response?.url || "");
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("File reading error"));
    });
  };

  const handlePartnerImageChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Basic validations before upload
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB
    if (!allowedTypes.includes(file.type)) {
      showToast("error", "Only JPG, PNG or WEBP images are allowed");
      if (event?.target) event.target.value = "";
      return;
    }
    if (file.size > maxSizeBytes) {
      showToast("error", "Image is too large (max 5MB)");
      if (event?.target) event.target.value = "";
      return;
    }

    setUploadingImage(true);
    clearFieldError("imageUrl");
    try {
      const url = await uploadImage(file);
      if (!url) {
        showToast("error", "Image upload failed");
        return;
      }
      setImageUrl(url);
      showToast("success", "Image uploaded successfully");
    } catch (error) {
      const msg =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Error uploading image";
      showToast("error", msg);
    } finally {
      setUploadingImage(false);
      if (event?.target) event.target.value = "";
    }
  };

  const fetchPartners = async () => {
    setLoadingPartners(true);
    try {
      const res = await axios.get(API_CONSTANTS.GET_PARTNERS(), {
        headers: API_CONSTANTS.headers(),
      });

      const list = res?.data?.response?.partners;
      setPartners(Array.isArray(list) ? list : []);
    } catch (error) {
      const msg =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load partners";
      showToast("error", msg);
    } finally {
      setLoadingPartners(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setFormErrors({});
    setLoading(true);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setLoading(false);
      return;
    }

    setSubmitting(true);
    try {
      const response = isEditMode
        ? await axios.put(
            API_CONSTANTS.UPDATE_PARTNER(),
            { ...payload, partnerId: selectedPartnerId },
            { headers: API_CONSTANTS.headers() }
          )
        : await axios.post(API_CONSTANTS.ADD_PARTNER(), payload, {
            headers: API_CONSTANTS.headers(),
          });

      const message =
        response?.data?.response?.message ||
        response?.data?.message ||
        (isEditMode ? "Partner updated successfully" : "Partner added successfully");

      showToast("success", message);

      setShowModal(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      const msg =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        (isEditMode ? "Failed to update partner" : "Failed to add partner");
      setErrorMessage(msg);
      showToast("error", msg);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleEditPartner = (partner) => {
    if (!partner) return;
    setSelectedPartnerId(partner.partnerId || "");
    setPartnerName(partner.partnerName || "");
    setImageUrl(partner.imageUrl || "");
    setCategory(partner.category || "");
    setPromoCode(partner.promoCode || "");
    setServicesInput(Array.isArray(partner.services) ? partner.services.join(", ") : "");
    setHowToUseInput(Array.isArray(partner.howToUse) ? partner.howToUse.join("\n") : "");
    setTermsInput(
      Array.isArray(partner.termsAndConditions)
        ? partner.termsAndConditions.join("\n")
        : ""
    );
    setStatus(partner.status || "active");
    setErrorMessage("");
    setShowModal(true);
  };

  const handleDeletePartner = async (partnerId) => {
    if (!partnerId) return;
    setDeleting(true);
    try {
      const response = await axios.delete(API_CONSTANTS.DELETE_PARTNER(), {
        headers: API_CONSTANTS.headers(),
        data: { partnerId },
      });

      const message =
        response?.data?.response?.message ||
        response?.data?.message ||
        "Partner deleted successfully";
      showToast("success", message);

      if (selectedPartnerId === partnerId) {
        resetForm();
      }
      setShowDeleteModal(false);
      setDeleteTarget({ partnerId: "", partnerName: "" });
      fetchPartners();
    } catch (error) {
      const msg =
        error?.response?.data?.response?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete partner";
      showToast("error", msg);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (partner) => {
    if (!partner?.partnerId) return;
    setDeleteTarget({
      partnerId: partner.partnerId,
      partnerName: partner.partnerName || "",
    });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteTarget({ partnerId: "", partnerName: "" });
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="m-0" style={{ fontWeight: 600 }}>
          Discount and Wellness
        </h5>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn"
            style={{ background: "#009B9414", border: "1px solid #5C5C5C" }}
            onClick={fetchPartners}
            disabled={loadingPartners}
          >
            {loadingPartners ? "Loading..." : "Refresh"}
          </button>

          <button type="button" className="btn btn-save" onClick={handleOpenAdd}>
            Add
          </button>
        </div>
      </div>

      <Modal show={showModal} size="lg" centered backdrop="static" onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? "Update Partner" : "Add Partner"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage ? (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Partner Name *</label>
                <input
                  className={`form-control ${formErrors?.partnerName ? "is-invalid" : ""}`}
                  value={partnerName}
                  onChange={(e) => {
                    setPartnerName(e.target.value);
                    clearFieldError("partnerName");
                  }}
                  placeholder="Shifa Pharmacy"
                />
                {formErrors?.partnerName ? (
                  <div className="invalid-feedback">{formErrors.partnerName}</div>
                ) : null}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Category *</label>
                <input
                  className={`form-control ${formErrors?.category ? "is-invalid" : ""}`}
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    clearFieldError("category");
                  }}
                  placeholder="Pharmacy"
                />
                {formErrors?.category ? (
                  <div className="invalid-feedback">{formErrors.category}</div>
                ) : null}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Promo Code</label>
                <input
                  className="form-control"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="SHIFA20"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Image *</label>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div
                    className="border rounded"
                    style={{ width: 64, height: 64, overflow: "hidden", background: "#f6f6f6" }}
                  >
                    {uploadingImage ? (
                      <div
                        className="d-flex align-items-center justify-content-center w-100 h-100"
                        style={{ color: "#888", fontSize: 10 }}
                      >
                        Uploading
                      </div>
                    ) : imageUrl && !imagePreviewError ? (
                      <img
                        src={imageUrl}
                        alt="partner"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={() => setImagePreviewError(true)}
                      />
                    ) : imageUrl && imagePreviewError ? (
                      <div
                        className="d-flex align-items-center justify-content-center w-100 h-100"
                        style={{ color: "#888", fontSize: 10 }}
                      >
                        No preview
                      </div>
                    ) : null}
                  </div>

                  <div style={{ minWidth: 260, flexGrow: 1 }}>
                    <input
                      type="file"
                      className={`form-control ${formErrors?.imageUrl ? "is-invalid" : ""}`}
                      accept="image/*"
                      onChange={handlePartnerImageChange}
                      disabled={uploadingImage}
                    />
                    {formErrors?.imageUrl ? (
                      <div className="invalid-feedback">{formErrors.imageUrl}</div>
                    ) : null}
                    <small style={{ color: "#888" }}>
                      {uploadingImage ? "Uploading image..." : imageUrl ? imageUrl : "No image uploaded"}
                    </small>
                  </div>

                  {imageUrl ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setImageUrl("");
                        setFieldError("imageUrl", "Partner image is required");
                      }}
                      disabled={uploadingImage}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Services (comma separated)</label>
                <input
                  className="form-control"
                  value={servicesInput}
                  onChange={(e) => setServicesInput(e.target.value)}
                  placeholder="Medicines, Lab Tests, Supplements"
                />
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">How To Use (one per line)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={howToUseInput}
                  onChange={(e) => setHowToUseInput(e.target.value)}
                  placeholder={
                    "Visit any Shifa Pharmacy branch\nShow your app promo code at the counter\nGet instant discount on your bill"
                  }
                />
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Terms & Conditions (one per line)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={termsInput}
                  onChange={(e) => setTermsInput(e.target.value)}
                  placeholder={
                    "Valid for app users only\nCannot be combined with other offers\nValid until 31st December 2025"
                  }
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-save"
                disabled={submitting || loading || uploadingImage}
              >
                {submitting || loading
                  ? "Saving..."
                  : uploadingImage
                  ? "Uploading..."
                  : isEditMode
                  ? "Save Changes"
                  : "Add"}
              </button>
              <button type="button" className="btn btn-discard" onClick={handleCloseModal}>
                Discard
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} centered backdrop="static" onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="m-0">
            Are you sure you want to delete{' '}
            <strong>{deleteTarget.partnerName || deleteTarget.partnerId}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-discard" onClick={closeDeleteModal}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-save"
            onClick={() => handleDeletePartner(deleteTarget.partnerId)}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>

      <div className="bg-white rounded p-4 mt-4" style={{ maxWidth: 1200 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="m-0" style={{ fontWeight: 600 }}>
            Partners
          </h6>
          <span style={{ color: "#888" }}>
            {Array.isArray(partners) ? partners.length : 0}
          </span>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Promo</th>
                <th>Status</th>
                <th>Services</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(partners || []).map((p) => (
                <tr key={p.partnerId}>
                  <td>{p.partnerName}</td>
                  <td>{p.category}</td>
                  <td>{p.promoCode}</td>
                  <td>{p.status}</td>
                  <td>{Array.isArray(p.services) ? p.services.join(", ") : ""}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEditPartner(p)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => openDeleteModal(p)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {(!partners || partners.length === 0) && !loadingPartners ? (
                <tr>
                  <td colSpan={6} style={{ color: "#888" }}>
                    No partners found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Toast
        cName={toast.cName}
        icon={toast.icon}
        text={toast.message}
        color={toast.iconColor}
      />
    </div>
  );
}
